import React, { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Chip from '../Common/Chip';
import { UseCaseSummary, UseCaseStatus } from '../../types';
import { getUseCaseStatusLabel } from './useCaseStatus';

export interface UseCaseTableProps {
  /** List of use cases to display */
  useCases: UseCaseSummary[];
  /** Loading state */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Domain name map by ID for display */
  domainNameById: Record<string, string>;
  /** Set of selected use case IDs */
  selectedUseCaseIds: Set<string>;
  /** Callback when selection changes */
  onToggleSelection: (useCaseId: string) => void;
  /** Set of use case IDs eligible for prioritization */
  eligibleForPrioritization: string[];
}

export const UseCaseTable: React.FC<UseCaseTableProps> = ({
  useCases,
  isLoading,
  error,
  domainNameById,
  selectedUseCaseIds,
  onToggleSelection,
  eligibleForPrioritization,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const statusVariant = (status: UseCaseStatus) =>
    status === 'Completed' ? 'success' : status === 'In Progress' ? 'warning' : 'info';

  // Calculate header checkbox state based on eligible items only
  const eligibleInTable = useCases.filter((uc) => eligibleForPrioritization.includes(uc.id));
  const selectedEligible = eligibleInTable.filter((uc) => selectedUseCaseIds.has(uc.id));
  const allEligibleSelected = eligibleInTable.length > 0 && selectedEligible.length === eligibleInTable.length;
  const someEligibleSelected = selectedEligible.length > 0 && selectedEligible.length < eligibleInTable.length;

  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.checked = allEligibleSelected;
      headerCheckboxRef.current.indeterminate = someEligibleSelected;
    }
  }, [allEligibleSelected, someEligibleSelected]);

  return (
    <div className="rounded-xl border border-border bg-card p-0 overflow-hidden">
      <div className="overflow-x-auto">
        {isLoading ? (
          <div className="p-6 text-sm text-muted-foreground">{t('global.loading')}</div>
        ) : error ? (
          <div className="p-6 text-sm text-red-600">{t('useCases.table.error')}</div>
        ) : (
          <table className="w-full table-fixed">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[2%]">
                  <input
                    ref={headerCheckboxRef}
                    type="checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        useCases.forEach((uc) => {
                          if (eligibleForPrioritization.includes(uc.id) && !selectedUseCaseIds.has(uc.id)) {
                            onToggleSelection(uc.id);
                          }
                        });
                      } else {
                        useCases.forEach((uc) => {
                          if (selectedUseCaseIds.has(uc.id)) {
                            onToggleSelection(uc.id);
                          }
                        });
                      }
                    }}
                    className="rounded border-input cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[27%]">
                  {t('useCases.table.columns.useCase')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[27%]">
                  {t('useCases.table.columns.description')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[9%]">
                  {t('global.domain')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[9%]">
                  {t('global.category')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[8%]">
                  {t('useCases.table.columns.status')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[9%]">
                  {t('global.businessValue')}
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground w-[9%]">
                  {t('global.technicalComplexity')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {useCases.map((u) => {
                const isEligible = eligibleForPrioritization.includes(u.id);
                const isSelected = selectedUseCaseIds.has(u.id);
                return (
                  <tr
                    key={u.id}
                    className={`cursor-pointer hover:bg-muted/30 ${isSelected ? 'bg-muted/50' : ''}`}
                    onClick={() => navigate(`/cas-usage/${u.id}`)}
                  >
                    <td className="px-4 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!isEligible}
                        onChange={() => onToggleSelection(u.id)}
                        className="rounded border-input cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="text-sm font-semibold text-foreground break-words">{u.name}</div>
                    </td>
                  <td className="px-4 py-3 text-sm text-foreground/80 align-top">
                    <span className="block break-words line-clamp-3" title={u.short_description || ''}>
                      {u.short_description || '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground/80 align-top break-words">
                    {(u as any).domain_id
                      ? domainNameById[(u as any).domain_id] || (u as any).domain_id
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground/80 align-top break-words">{u.category || '—'}</td>
                  <td className="px-4 py-3 text-sm align-top">
                    <Chip label={getUseCaseStatusLabel(u.status, t)} variant={statusVariant(u.status) as any} />
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground/80 align-top">
                    {typeof u.value_score === 'number' ? u.value_score : '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-foreground/80 align-top">
                    {typeof u.complexity_score === 'number' ? u.complexity_score : '—'}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
